/**
 * LockSlide - Slide 7
 * Training summary and lock/save actions
 */

import { useEffect, useState } from "react";
import { AlertTriangle, FileBox, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckpointSelector } from "@/components/loras/CheckpointSelector";
import { cn } from "@/lib/utils";
import type { TrainingSummary } from "../types";
import { MODELS } from "../types";
import type { Checkpoint, Lora } from "@/types/loras";

interface LockSlideProps {
  summary: TrainingSummary | null;
  onSetTriggerWord: (word: string) => void;
  onLock: () => void;
  onSaveAsDraft: () => void;
  /** Real-mode signal: trigger-word mutation is in flight. */
  realModeUpdatingTrigger?: boolean;
  /** When provided, this is a real backend LoRA — relabel Lock/Draft. */
  realModeLora?: Lora | undefined;
  /** Real-mode (Feature 2): checkpoints to (re-)pick the active one from. */
  realModeCheckpoints?: Checkpoint[];
  /** Real-mode: the LoRA's current default checkpoint id. */
  realModeDefaultCheckpointId?: string | null;
  /** Real-mode: persist the chosen checkpoint. */
  onSelectCheckpoint?: (checkpointId: string) => void;
  /** Real-mode: set-default mutation in flight. */
  realModeSettingDefault?: boolean;
}

export function LockSlide({
  summary,
  onSetTriggerWord,
  onLock,
  onSaveAsDraft,
  realModeUpdatingTrigger,
  realModeLora,
  realModeCheckpoints,
  realModeDefaultCheckpointId,
  onSelectCheckpoint,
  realModeSettingDefault,
}: LockSlideProps) {
  const realMode = realModeLora !== undefined;
  // Trigger-word draft is debounced on blur in real mode so we don't fire
  // a mutation on every keystroke.
  const [triggerDraft, setTriggerDraft] = useState(summary?.triggerWord ?? "");
  useEffect(() => {
    if (summary) setTriggerDraft(summary.triggerWord);
  }, [summary?.triggerWord]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!summary) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-muted-foreground">
          {realMode
            ? "Training has not finished yet. Come back once the wizard reports completion."
            : "No training summary available."}
        </p>
      </div>
    );
  }

  const model = MODELS.find((m) => m.id === summary.baseModel);
  const formattedDate = summary.trainedAt.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const formattedTime = summary.trainedAt.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const likenessColor =
    summary.likenessScore >= 85
      ? "text-green-500"
      : summary.likenessScore >= 75
        ? "text-amber-500"
        : "text-red-500";

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-3">
          Lock to character.
        </h2>
        <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
          Once locked, this LoRA replaces the visual reference set when shots
          pull from this character. Behavioural traits and motion profile still
          apply on top.
        </p>
      </div>

      {/* Two column layout */}
      <div className="flex gap-8 max-w-4xl">
        {/* Left column - Training summary */}
        <div className="flex-1">
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-4">
            Training Summary
          </h3>

          <div className="rounded-lg border border-white/10 bg-[#1a1a1a] p-5 space-y-4">
            {/* Dataset */}
            <SummaryRow
              label="Dataset"
              value={`${summary.datasetSize} images · ${summary.lightingConditions} lighting conditions`}
            />

            {/* Trigger word - Editable */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Trigger word
              </span>
              <div className="flex items-center gap-2">
                {realModeUpdatingTrigger && (
                  <Loader2 className="w-3 h-3 text-muted-foreground animate-spin" />
                )}
                <Input
                  value={triggerDraft}
                  onChange={(e) => {
                    setTriggerDraft(e.target.value);
                    if (!realMode) onSetTriggerWord(e.target.value);
                  }}
                  onBlur={() => {
                    if (realMode && triggerDraft !== summary.triggerWord) {
                      onSetTriggerWord(triggerDraft);
                    }
                  }}
                  disabled={Boolean(realModeUpdatingTrigger)}
                  className="w-40 h-7 text-sm font-mono bg-white/5 border-white/10 text-right"
                />
              </div>
            </div>

            {/* Base model */}
            <SummaryRow
              label="Base model"
              value={summary.baseModelLabel || model?.name || summary.baseModel}
            />

            {/* Rank · Epochs */}
            <SummaryRow
              label="Rank · Epochs"
              value={`${summary.rank} · ${summary.epochs}`}
            />

            {/* Final loss */}
            <SummaryRow
              label="Final loss"
              value={summary.finalLoss.toFixed(4)}
              mono
            />

            {/* Likeness */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Likeness</span>
              <span className={cn("text-sm font-mono", likenessColor)}>
                {summary.likenessScore} / 100
              </span>
            </div>

            {/* Trained on */}
            <SummaryRow
              label="Trained on"
              value={`${formattedDate} · ${formattedTime}`}
            />

            {/* GPU time */}
            <SummaryRow
              label="GPU time"
              value={`${summary.gpuMinutes} min · $${summary.cost.toFixed(2)}`}
            />
          </div>

          {/* Active checkpoint (Feature 2) — pick which checkpoint every
              future generation uses. Surfaced here too so a previously-trained
              LoRA can re-pick without re-running training. */}
          {realMode &&
            realModeCheckpoints &&
            realModeCheckpoints.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                  Active checkpoint
                </h3>
                <p className="mb-3 text-[11px] text-muted-foreground">
                  This checkpoint is used for every future generation with this
                  LoRA. The highest step is selected by default.
                </p>
                <CheckpointSelector
                  checkpoints={realModeCheckpoints}
                  defaultCheckpointId={realModeDefaultCheckpointId}
                  onSelect={(id) => onSelectCheckpoint?.(id)}
                  settingDefault={realModeSettingDefault}
                />
              </div>
            )}
        </div>

        {/* Right column - Artifact */}
        <div className="w-80">
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-4">
            Artifact
          </h3>

          <div className="rounded-lg border border-white/10 bg-[#1a1a1a] p-5">
            {/* File icon */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileBox className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-mono text-foreground">
                  {summary.artifactFilename}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {summary.artifactSize} ·{" "}
                  {summary.baseModelLabel || model?.name || "FLUX"} · RANK{" "}
                  {summary.rank}
                </p>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 mb-6">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-amber-500 font-medium">Heads up</p>
                <p className="text-[10px] text-amber-500/70 mt-0.5 leading-relaxed">
                  {realMode
                    ? `The LoRA flipped to "${realModeLora?.statusSlug ?? "ready"}" on its first checkpoint. There is no separate lock step — the trigger word above is the only remaining setting.`
                    : "Locking will change how all existing shots that link this character render. Review affected shots before continuing."}
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              {/* For a ready LoRA, there's nothing to "finalize" - just show Close */}
              {realMode && realModeLora?.statusSlug === "ready" ? (
                <Button
                  variant="pink"
                  size="lg"
                  className="w-full"
                  onClick={onSaveAsDraft}
                >
                  Close
                </Button>
              ) : (
                <>
                  <Button
                    variant="pink"
                    size="lg"
                    className="w-full"
                    onClick={onLock}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {realMode ? "Done" : "Lock LoRA to character"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    className="w-full"
                    onClick={onSaveAsDraft}
                  >
                    {realMode ? "Close" : "Save as draft instead"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Summary row component
interface SummaryRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

function SummaryRow({ label, value, mono = false }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn("text-sm text-foreground", mono && "font-mono")}>
        {value}
      </span>
    </div>
  );
}
